import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import TimerComponent from "./TimerComponent";
import mosqueBg from "../../assets/images/eid/mosque_bg.png";
import giftIcons from "../../assets/images/eid/gift_icons.png"; // We can slice this or use as placeholder
import coinIcon from "../../assets/images/weekly star image/New folder/ic_coin 4.png";
import fallbackAvatar from "../../assets/icons/selected_male.png";

// SVGs for UI elements
const BackIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 12H5M12 19l-7-7 7-7" />
  </svg>
);

const RulesIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#00e676" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const RewardsIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#00e676" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 12V8H4v4" />
    <path d="M2 11h20v3H2z" />
    <path d="M12 11V7a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v4" />
    <path d="M12 11V7a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v4" />
    <rect x="4" y="14" width="16" height="7" rx="2" />
  </svg>
);

const Design = ({
  eventDetails,
  topUsers,
  rewards,
  loading
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("senders"); // "senders" or "receivers"
  const [showScreen, setShowScreen] = useState("main"); // "main", "rules", "rewards"
  const [imageErrors, setImageErrors] = useState({});

  const ImageBaseUrl = "https://media.sh8afchat.com/";

  const handleError = (imageUrl) => {
    setImageErrors((prev) => ({ ...prev, [imageUrl]: true }));
  };

  const getAvatar = (url) => (imageErrors[url] ? fallbackAvatar : `${ImageBaseUrl}${url}`);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b1a10] flex items-center justify-center">
        <div className="text-[#00e676] text-xl font-bold animate-pulse">{t("Loading...")}</div>
      </div>
    );
  }

  const eligibleGifts = eventDetails?.details?.gifts || [];
  const leaderboardData = activeTab === "senders" ? topUsers?.top_senders || [] : topUsers?.top_receivers || [];
  const currentUserRank = activeTab === "senders" ? topUsers?.user_sender : topUsers?.user_receiver;

  const RulesScreen = () => (
    <motion.div 
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "100%" }}
      className="fixed inset-0 z-[100] bg-[#0b1a10] overflow-y-auto pb-10"
    >
      <div className="flex items-center gap-4 p-4 border-b border-white/5">
        <button onClick={() => setShowScreen("main")} className="text-[#00e676] p-2">
          <BackIcon />
        </button>
        <h2 className="text-white text-xl font-bold">{t("Event Rules")}</h2>
      </div>
      
      <div className="p-6 space-y-8">
        <section>
          <div className="flex items-center gap-2 text-[#00e676] mb-4">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
            <h3 className="text-xl font-bold">{t("Event Description")}</h3>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
             <p className="text-white/80">{eventDetails?.details?.description || t("No description available")}</p>
          </div>
        </section>

        <section>
          <div className="flex justify-between items-center mb-4">
             <div className="flex items-center gap-2 text-[#00e676]">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 12V8H4v4" /><path d="M2 11h20v3H2z" /><path d="M12 11V7a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v4" /><path d="M12 11V7a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v4" /><rect x="4" y="14" width="16" height="7" rx="2" /></svg>
                <h3 className="text-xl font-bold">{t("Eligible Gifts")}</h3>
             </div>
             <span className="bg-[#00e676]/20 text-[#00e676] text-xs px-3 py-1 rounded-full">{eligibleGifts.length} {t("Selected Gifts")}</span>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {eligibleGifts.map((gift, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-3 flex flex-col items-center">
                <div className="w-12 h-12 mb-2 flex items-center justify-center">
                  <img src={`${ImageBaseUrl}${gift.image}`} className="w-full h-full object-contain" alt={gift.name} />
                </div>
                <span className="text-white text-[10px] text-center mb-1 truncate w-full">{gift.name}</span>
                <div className="flex items-center gap-1 text-yellow-500 text-[10px]">
                  <img src={coinIcon} className="w-2 h-2" alt="coin" />
                  {gift.price}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </motion.div>
  );

  const RewardsScreen = () => {
    const top1Rewards = rewards?.top_1 || [];
    const top2Rewards = rewards?.top_2 || [];
    const top3Rewards = rewards?.top_3 || [];

    return (
      <motion.div 
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        className="fixed inset-0 z-[100] bg-[#0b1a10] overflow-y-auto pb-10"
      >
        <div className="flex items-center gap-4 p-4 border-b border-white/5">
          <button onClick={() => setShowScreen("main")} className="text-[#00e676] p-2">
            <BackIcon />
          </button>
          <h2 className="text-white text-xl font-bold">{t("Event Rewards")}</h2>
        </div>

        <div className="p-4">
          <div className="bg-gradient-to-br from-[#1b4332] to-[#081c15] rounded-3xl p-8 mb-8 text-center border border-white/5 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#00e676]/10 blur-3xl rounded-full" />
            <h1 className="text-3xl font-bold text-white mb-2">{t("Season Rewards")}</h1>
            <p className="text-[#00e676] font-bold">{t(eventDetails?.event_name)}</p>
          </div>

          <div className="space-y-8">
            {[1, 2, 3].map(pos => {
              const posRewards = pos === 1 ? top1Rewards : pos === 2 ? top2Rewards : top3Rewards;
              return (
                <div key={pos}>
                   <div className="flex items-center gap-2 text-yellow-500 mb-4">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" /><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" /><path d="M4 22h16" /><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" /><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" /><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" /></svg>
                    <h3 className="text-xl font-bold">{pos}{pos === 1 ? "st" : pos === 2 ? "nd" : "rd"} Place</h3>
                  </div>
                  {posRewards.length > 0 ? (
                    <div className="grid grid-cols-2 gap-4">
                      {posRewards.map((reward, i) => (
                        <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center space-y-3">
                          <div className="w-12 h-12 mx-auto flex items-center justify-center">
                            <img src={`${ImageBaseUrl}${reward.image}`} className="w-full h-full object-contain" alt={reward.name} />
                          </div>
                          <h4 className="text-white font-bold text-sm">{reward.name}</h4>
                          <p className="text-white/40 text-[10px]">{reward.description || t("Exclusive reward")}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center">
                      <p className="text-white/40 text-sm">{t("No rewards specified yet")}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="relative min-h-screen bg-[#0b1a10] text-white font-['Tajawal',_sans-serif] overflow-x-hidden pt-12">
 
        {showScreen === "rules" && <RulesScreen />}
        {showScreen === "rewards" && <RewardsScreen />}
 
      {/* Background elements */}
      <div className="fixed top-0 left-0 w-full h-[60vh] z-0 pointer-events-none opacity-40">
        <img src={mosqueBg} className="w-full h-full object-cover" alt="" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0b1a10]/50 to-[#0b1a10]" />
      </div>

      {/* Top Controls */}
      <div className="relative z-20 flex justify-between items-center px-4 mb-8">
        <button onClick={() => setShowScreen("rewards")} className="flex flex-col items-center gap-1 group">
          <div className="p-3 bg-white/5 rounded-2xl group-active:scale-95 transition-transform">
            <RewardsIcon />
          </div>
          <span className="text-[10px] text-white/60 uppercase font-bold">{t("Rewards")}</span>
        </button>
        
        <div className="text-center">
           <h1 className="text-3xl font-black text-white tracking-tight leading-none mb-1">{t(eventDetails?.event_name) || t("Eid al-Fitr Mubarak")}</h1>
           <p className="text-white/40 text-xs">{t("Celebrate Eid with your friends and share gifts")}</p>
        </div>

        <button onClick={() => setShowScreen("rules")} className="flex flex-col items-center gap-1 group">
          <div className="p-3 bg-white/5 rounded-2xl group-active:scale-95 transition-transform">
            <RulesIcon />
          </div>
          <span className="text-[10px] text-white/60 uppercase font-bold">{t("Rules")}</span>
        </button>
      </div>

      {/* Timer */}
      <div className="relative z-20 mb-10 text-center">
         <p className="text-white/40 text-[10px] uppercase font-bold mb-1">{t("Event ends in")}:</p>
         <TimerComponent initialTime={eventDetails?.details?.remainingTime || { day: 0, hour: 0, minute: 0, second: 0 }} />
      </div>

      {/* Eligible Gifts */}
      <div className="relative z-20 px-4 mb-10">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-[#00e676] font-bold text-sm uppercase tracking-wider">{t("Eligible Gifts")}</h3>
          <button className="text-[10px] text-white/40 uppercase font-black" onClick={() => setShowScreen("rules")}>{t("View All")}</button>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {eligibleGifts.slice(0, 3).map((gift, i) => (
            <div key={i} className="bg-white/5 border border-white/10 rounded-3xl p-4 flex flex-col items-center group active:scale-95 transition-transform">
              <div className="w-12 h-12 bg-[#00e676]/10 rounded-2xl flex items-center justify-center text-[#00e676] mb-3 overflow-hidden">
                 <img src={`${ImageBaseUrl}${gift.image}`} className="w-full h-full object-contain p-1" alt={gift.name} />
              </div>
              <span className="text-[10px] font-bold mb-1 truncate w-full text-center">{gift.name}</span>
              <span className="text-[#00e676] text-[10px] font-black">{gift.price}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Leaderboard Section */}
      <div className="relative z-20 bg-black/20 rounded-t-[40px] px-4 pt-8 pb-32 border-t border-white/5">
        {/* Tab Switcher */}
        <div className="flex bg-black/40 p-1 rounded-full mb-8">
          <button 
            onClick={() => setActiveTab("senders")}
            className={`flex-1 py-3 rounded-full text-xs font-bold transition-all ${activeTab === "senders" ? "bg-[#00e676] text-black" : "text-white/40 hover:text-white/60"}`}
          >
            {t("Top Senders")}
          </button>
          <button 
            onClick={() => setActiveTab("receivers")}
            className={`flex-1 py-3 rounded-full text-xs font-bold transition-all ${activeTab === "receivers" ? "bg-[#00e676] text-black" : "text-white/40 hover:text-white/60"}`}
          >
            {t("Top Receivers")}
          </button>
        </div>

        {/* Podium for Top 3 */}
        <div className="podium flex items-end justify-center mb-10 h-48 gap-2">
           {/* Rank 2 */}
           <div className="flex-1 flex flex-col items-center">
              <div className="relative mb-2">
                <img 
                  src={getAvatar(leaderboardData[1]?.avatar)} 
                  className="w-16 h-16 rounded-full border-2 border-white/20 object-cover bg-white/10"
                  onError={() => handleError(leaderboardData[1]?.avatar)}
                  alt=""
                />
                <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-gray-400 rounded-full flex items-center justify-center text-[10px] font-bold text-black border-2 border-[#0b1a10]">2</div>
              </div>
              <p className="text-[10px] font-bold truncate w-full text-center mb-1">{leaderboardData[1]?.name || "---"}</p>
              <p className="text-[#00e676] text-[10px] font-black">{leaderboardData[1]?.totalGiftNum || 0}</p>
           </div>

           {/* Rank 1 */}
           <div className="flex-[1.2] flex flex-col items-center scale-110">
              <div className="relative mb-3">
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-yellow-500 animate-bounce">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>
                </div>
                <img 
                  src={getAvatar(leaderboardData[0]?.avatar)} 
                  className="w-20 h-20 rounded-full border-4 border-yellow-500 object-cover shadow-[0_0_20px_rgba(234,179,8,0.3)] bg-white/10"
                  onError={() => handleError(leaderboardData[0]?.avatar)}
                  alt=""
                />
                <div className="absolute -bottom-2 -right-1 w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center text-xs font-black text-black border-4 border-[#0b1a10]">1</div>
              </div>
              <p className="text-xs font-black truncate w-full text-center mb-1 text-white">{leaderboardData[0]?.name || "---"}</p>
              <p className="text-[#00e676] text-xs font-black">{leaderboardData[0]?.totalGiftNum || 0}</p>
           </div>

           {/* Rank 3 */}
           <div className="flex-1 flex flex-col items-center">
              <div className="relative mb-2">
                <img 
                  src={getAvatar(leaderboardData[2]?.avatar)} 
                  className="w-16 h-16 rounded-full border-2 border-white/20 object-cover bg-white/10"
                  onError={() => handleError(leaderboardData[2]?.avatar)}
                  alt=""
                />
                <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-amber-700 rounded-full flex items-center justify-center text-[10px] font-bold text-black border-2 border-[#0b1a10]">3</div>
              </div>
              <p className="text-[10px] font-bold truncate w-full text-center mb-1">{leaderboardData[2]?.name || "---"}</p>
              <p className="text-[#00e676] text-[10px] font-black">{leaderboardData[2]?.totalGiftNum || 0}</p>
           </div>
        </div>

        {/* List for 4+ */}
        <div className="space-y-3">
          {leaderboardData.slice(3).map((user, i) => (
            <div key={i} className={`flex items-center gap-4 p-3 rounded-2xl border border-white/5 transition-colors bg-black/20`}>
               <div className="w-8 flex justify-center text-xs font-bold text-white/40">
                  {i + 4}
               </div>
               <div className="relative">
                  <img 
                    src={getAvatar(user.avatar)} 
                    className="w-10 h-10 rounded-full object-cover bg-white/10" 
                    alt="" 
                    onError={()=>handleError(user.avatar)}
                  />
               </div>
               <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold truncate">{user.name}</p>
                  <p className="text-[10px] text-white/40 font-bold uppercase tracking-widest">{user.totalGiftNum} {t("Points")}</p>
               </div>
               <div className="text-white/20">
                  <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M9 18l6-6-6-6" /></svg>
               </div>
            </div>
          ))}
          {leaderboardData.length === 0 && (
            <div className="text-center py-10 text-white/20 text-sm">
              {t("No data available")}
            </div>
          )}
        </div>
      </div>

      {/* Sticky User Rank Footer */}
      {currentUserRank && (
        <div className="fixed bottom-4 left-4 right-4 z-[50]">
          <div className="bg-[#00e676] rounded-[24px] p-4 flex items-center justify-between shadow-[0_8px_32px_rgba(0,230,118,0.3)] border border-white/40">
             <div className="flex flex-col">
                <span className="text-black/60 text-[10px] font-black uppercase tracking-widest leading-none">{currentUserRank.totalGiftNum}</span>
                <span className="text-black font-black text-xs uppercase">{t("Points")}</span>
             </div>
             <div className="flex flex-col items-center">
                <span className="text-black font-black text-sm">{currentUserRank.name}</span>
                <span className="text-black/60 text-[10px] font-bold uppercase tracking-tighter">{t("Current ID")}: {currentUserRank.uuid}</span>
             </div>
             <div className="relative">
                <img 
                  src={getAvatar(currentUserRank.avatar)} 
                  className="w-10 h-10 rounded-full border-2 border-black/10 shadow-lg bg-white/10" 
                  alt="" 
                  onError={() => handleError(currentUserRank.avatar)}
                />
             </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@200;300;400;500;700;800;900&display=swap');
        
        body {
           background-color: #0b1a10;
        }

        .podium {
           perspective: 1000px;
        }

        ::-webkit-scrollbar {
          width: 0px;
        }
      `}</style>
    </div>
  );
};

export default Design;
